import { Dispatch, ReactNode, SetStateAction } from 'react';
import {
  Alert,
  Collapse,
  Grid,
  InputNumber,
  Space,
  Table,
  Tooltip,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  SolutionOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import Column from 'antd/es/table/Column';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  GuestItem,
  getInvitationStatus,
  invitationStatusTranslation,
  useFetchHost,
  useFetchWaitingList,
} from '@seaters/data-access';
import {
  AssignationMode,
  GuestEntity,
  InvitationEntity,
  InvitationsListEntity,
  InvitationStatusEnum,
  SemanticNameEnum,
} from '@seaters/constants';
import { Button, CollapseButton, statusToBadge } from '@seaters/ui';
import { InvitationsActions } from '../ActionsButtons/InvitationsActions';
import { useGuestInvitation } from '../helpers/guestInvitation';
import { useDeclineInvitation } from '../hooks';

const { useBreakpoint } = Grid;

type InvitationListTableProps = {
  groupGuests: GuestItem[];
  guestInvitation: ReturnType<typeof useGuestInvitation>;
  allocatedInvitation: GuestItem | undefined;
  totalTickets: number;
  totalTicketsToIgnore: number;
  nonAllocatedTickets: number;
  nonAllocatedTicketsWithOptional: number;
  maxTicketsPerHost: number | undefined;
  handleRemoveGuest: (guest: GuestEntity, tickets: number) => void;
  handleTotalTickets: (value: number) => void;
  handleTotalTicketsSubmit: () => void;
  host: ReturnType<typeof useFetchHost>['data'];
  waitingList: ReturnType<typeof useFetchWaitingList>['data'];
  handleDeclineInvitation: ReturnType<typeof useDeclineInvitation>;
  confirmAcceptInvitation: (invitation: InvitationEntity) => void;
  showEditModal: (guest: GuestEntity) => void;
  showChangeTickets: () => void;
  hideChangeTickets: () => void;
  showBusinessReasonModal: () => void;
  setTickets: (guestId: string, tickets: number) => void;
  totalTicketsChange: boolean;
  businessReason: string | undefined;
  setBusinessReason: Dispatch<SetStateAction<string | undefined>>;
  disabledChangeTickets: boolean | undefined;
  invitations: InvitationsListEntity;
  isFetching?: boolean;
  assignationMode: AssignationMode | undefined;
  renderFeedbackCell?: (invitation: InvitationEntity | undefined) => ReactNode;
  renderBudgetCell?: (invitation: InvitationEntity | undefined) => ReactNode;
};

export function InvitationListTable({
  groupGuests,
  guestInvitation,
  allocatedInvitation,
  totalTickets,
  totalTicketsToIgnore,
  nonAllocatedTickets,
  nonAllocatedTicketsWithOptional,
  maxTicketsPerHost,
  handleRemoveGuest,
  handleTotalTickets,
  handleTotalTicketsSubmit,
  host,
  waitingList,
  handleDeclineInvitation,
  confirmAcceptInvitation,
  showEditModal,
  showChangeTickets,
  hideChangeTickets,
  showBusinessReasonModal,
  setTickets,
  totalTicketsChange,
  businessReason,
  setBusinessReason,
  disabledChangeTickets,
  invitations,
  isFetching,
  assignationMode,
  renderFeedbackCell,
  renderBudgetCell,
}: InvitationListTableProps) {
  const { t } = useTranslation();
  const screens = useBreakpoint();

  return (
    <Collapse
      size="large"
      expandIcon={({ isActive }) => {
        return <CollapseButton isActive={isActive} />;
      }}
      expandIconPosition="end"
      defaultActiveKey={['1']}
      items={[
        {
          key: '1',
          label: `${t('guestlist_invitation_table_title')} (${
            groupGuests.length
          })`,
          children: (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Table
                dataSource={groupGuests}
                pagination={false}
                loading={isFetching}
                scroll={{
                  x: true,
                }}
                footer={() => (
                  <Space>
                    {totalTicketsChange ? (
                      <>
                        <InputNumber
                          aria-label={t('guestlist_seats') ?? undefined}
                          min={
                            nonAllocatedTickets
                            // guestTickets - allocatedTickets - totalTicketsToIgnore
                          }
                          max={maxTicketsPerHost}
                          value={totalTickets}
                          onChange={(value) =>
                            handleTotalTickets(Number(value))
                          }
                        />
                        <Button onClick={hideChangeTickets}>
                          {t('button_text_cancel')}
                        </Button>
                        <Button
                          type="primary"
                          onClick={handleTotalTicketsSubmit}
                        >
                          {t('button_text_submit')}
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={showChangeTickets}
                        disabled={disabledChangeTickets}
                        icon={
                          businessReason && <CheckOutlined rev={undefined} />
                        }
                      >
                        {t('guestlist_total_tickets_change')}
                      </Button>
                    )}
                  </Space>
                )}
              >
                {/*  HELPER COLUMNS TO BETTER DEBUG: />*/}
                {/* <Column
                title={t('order_column_title')}
                key="order"
                dataIndex="order"
                width="5%"
              />
              <Column
                title={t('ID')}
                dataIndex="id"
                key="id"
                render={(_, { guest, isToAllocated }: GuestItem) => {
                  const invitation = guestInvitation(guest.guestId);

                  return <span>{invitation?.id} </span>;
                }}
              /> */}
                <Column
                  title={t('guestlist_status')}
                  key="status"
                  render={(_, { guest, tickets }: GuestItem) => {
                    const invitation = guestInvitation(guest.guestId);
                    // Use the computed status directly to properly handle position rejections
                    const effective = getInvitationStatus(invitation);
                    const translated = effective
                      ? invitationStatusTranslation[effective]
                      : null;

                    if (!effective) {
                      return (
                        <Button
                          type="link"
                          danger
                          onClick={() => handleRemoveGuest(guest, tickets)}
                          icon={<CloseOutlined rev={undefined} />}
                          aria-label={`${t('remove')} ${guest.firstName} ${
                            guest.lastName
                          }`}
                        >
                          {t('remove')}
                        </Button>
                      );
                    }

                    const badge = (statusToBadge as any)[effective];
                    const messageText = translated
                      ? (t(translated) as unknown as string)
                      : String(effective).split('_').join(' ');

                    return (
                      <Alert
                        style={{
                          width: 'fit-content',
                          fontSize: screens.xs ? '10px' : '14px',
                        }}
                        message={messageText}
                        showIcon
                        type={badge?.status || 'info'}
                        icon={badge?.icon}
                      />
                    );
                  }}
                />
                {/* PROTOTYPE (invite-management thread A): organiser feedback column */}
                {renderFeedbackCell && (
                  <Column
                    title={t('guestlist_organiser_feedback', {
                      defaultValue: 'Organiser feedback',
                    })}
                    key="proto-feedback"
                    render={(_, { guest }: GuestItem) =>
                      renderFeedbackCell(guestInvitation(guest.guestId))
                    }
                  />
                )}
                <Column
                  title={t('guestlist_seats')}
                  dataIndex="tickets"
                  key="tickets"
                  render={(_, { guest, tickets, isToAllocated }: GuestItem) => {
                    const invitation = guestInvitation(guest.guestId);
                    const status = getInvitationStatus(invitation);

                    const seatsLeft =
                      totalTickets -
                      nonAllocatedTicketsWithOptional +
                      totalTicketsToIgnore;

                    const maxValue = tickets + seatsLeft;

                    // to allocate: totalTickets - all host tickets
                    return (
                      <InputNumber
                        aria-label={t('guestlist_seats') ?? undefined}
                        min={isToAllocated ? undefined : 1}
                        max={isToAllocated ? undefined : maxValue}
                        defaultValue={
                          waitingList?.minNumberOfSeatsPerPosition || 1
                        }
                        onChange={(value) => {
                          setTickets(guest.guestId, Number(value));
                          if (allocatedInvitation) {
                            const inv = guestInvitation(
                              allocatedInvitation.guest.guestId
                            );
                            if (
                              !(
                                inv?.status === 'APPROVED' &&
                                (!inv.position ||
                                  inv.position.seatsRequestStatus ===
                                    'DECLINED')
                              ) &&
                              inv?.status !== 'CANCELLED' &&
                              inv?.status !== 'REJECTED'
                            ) {
                              if (allocatedInvitation) {
                                setTickets(
                                  allocatedInvitation.guest.guestId,
                                  (allocatedInvitation.tickets ?? 0) -
                                    ((value ?? 0) - tickets)
                                );
                              }
                            }
                          }
                        }}
                        value={tickets}
                        disabled={
                          isToAllocated ||
                          (invitation
                            ? status !== InvitationStatusEnum.BEING_REVIEWED ||
                              !!invitation?.numberOfNonAllocatedSeats ||
                              totalTicketsChange
                            : totalTicketsChange)
                        }
                      />
                    );
                  }}
                />
                <Column
                  title={t('guestlist_name')}
                  dataIndex="name"
                  key="name"
                  render={(_, { guest, isToAllocated }: GuestItem) => {
                    const invitation = guestInvitation(guest.guestId);

                    const internalEmployee = groupGuests.find(
                      (el) => el.guest.guestId === invitation?.specialGuestId
                    );

                    const isPublicOfficial =
                      guest.customInfos.find(
                        (customInfo) =>
                          customInfo.semanticName ===
                          SemanticNameEnum.PUBLIC_OFFICIAL
                      )?.informationValue === 'true';

                    return (
                      <span>
                        {isToAllocated ? (
                          t('not_allocated')
                        ) : (
                          <Tooltip
                            title={
                              internalEmployee
                                ? `Internal employee: ${internalEmployee?.guest.firstName} ${internalEmployee?.guest.lastName}`
                                : false
                            }
                          >
                            {guest.firstName} {guest.lastName}{' '}
                            {guest.guestId === host?.userId && `(${t('host')})`}
                          </Tooltip>
                        )}
                        {isPublicOfficial && (
                          <Tooltip title={t('public_official')}>
                            <SolutionOutlined rev={undefined} />
                          </Tooltip>
                        )}
                        {invitation?.specialGuest && (
                          <Tooltip title={'Internal employee'}>
                            <TeamOutlined rev={undefined} />
                          </Tooltip>
                        )}{' '}
                        {!isToAllocated && guest.guestId !== host?.userId && (
                          <EditOutlined
                            onClick={() => showEditModal(guest)}
                            rev={undefined}
                          />
                        )}
                      </span>
                    );
                  }}
                />
                <Column
                  title={t('guestlist_company')}
                  dataIndex="company"
                  key="company"
                  render={(_, { guest }: GuestItem) => {
                    return (
                      <span>
                        {guest.customInfos?.find(
                          (customInfo) =>
                            customInfo.semanticName === SemanticNameEnum.COMPANY
                        )?.informationValue || '–'}
                      </span>
                    );
                  }}
                />
                <Column
                  title={t('guestlist_last_rsvp')}
                  dataIndex="lastInvitation"
                  key="lastInvitation"
                  render={(_, { guest }: GuestItem) => {
                    const date = guest.customInfos?.find(
                      (customInfo) =>
                        customInfo.semanticName === SemanticNameEnum.LAST_RSVP
                    )?.informationValue;

                    // Custom-info values can be booleans; only a date
                    // string is renderable here.
                    return typeof date === 'string' ? (
                      <span>{dayjs(date).fromNow()}</span>
                    ) : null;
                  }}
                />
                <Column
                  title={t('guestlist_attendance')}
                  dataIndex="lastInvitation"
                  key="lastInvitation"
                  render={(_, { guest }: GuestItem) => {
                    return (
                      <span>
                        {`${
                          guest.customInfos?.find(
                            (customInfo) =>
                              customInfo.semanticName ===
                              SemanticNameEnum.ATTENDANCE
                          )?.informationValue || 0
                        }/${
                          guest.customInfos?.find(
                            (customInfo) =>
                              customInfo.semanticName === SemanticNameEnum.RSVPS
                          )?.informationValue || 0
                        }`}
                      </span>
                    );
                  }}
                />
                <Column
                  title={t('guestlist_cost_contact')}
                  dataIndex="lastInvitation"
                  key="lastInvitation"
                  render={(_, { guest }: GuestItem) => {
                    const cost =
                      guest.customInfos?.find(
                        (customInfo) =>
                          customInfo.semanticName ===
                          SemanticNameEnum.CONTACT_COST
                      )?.informationValue || 0;

                    return (
                      <div className="flex flex-col gap-1">
                        <span>
                          {Number(cost) ? `${Number(cost).toFixed(0)} €` : '–'}
                        </span>
                        {renderBudgetCell?.(guestInvitation(guest.guestId))}
                      </div>
                    );
                  }}
                />
                <Column
                  key="actions"
                  align="right"
                  render={(_, { guest, isToAllocated }: GuestItem) => {
                    return (
                      <InvitationsActions
                        guest={guest}
                        isToAllocated={!!isToAllocated}
                        hostUserId={host?.userId}
                        setBusinessReason={setBusinessReason}
                        showBusinessReasonModal={showBusinessReasonModal}
                        showEditModal={showEditModal}
                        confirmAcceptInvitation={confirmAcceptInvitation}
                        handleDeclineInvitation={handleDeclineInvitation}
                        invitationsCount={invitations.content.length}
                        assignationMode={assignationMode}
                      />
                    );
                  }}
                />
              </Table>
            </Space>
          ),
        },
      ]}
    />
  );
}
